// /auth/connect — approve a device-link sign-in ("click a link, the agent is
// in"). The counterpart of `s3p connect`:
//
//   The agent parked an ephemeral public key at
//   `.s3p/device-login/{deviceId}/request.json` and printed ONE url — this
//   page. We show who's asking (hostname, when), and on Approve we seal THIS
//   session's refresh token (the same thing an "agent code" has always been)
//   to that public key and write the answer next to the request. The agent
//   polls, decrypts, done. Nothing to copy, ever.
//
// The relay I/O and the sealing live in the SDK — `app.deviceLink`
// (getRequest/approve over VISITOR STS, crypto from @s3p/sdk/device-link).
// Being signed in is required only for having a refresh token worth handing
// over. The deviceId arrives in the #fragment, which browsers never send to
// servers.

import { Alert, Button, Card, Code, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { IconLink, IconShieldCheck } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import type { DeviceLinkModule } from "@s3p/sdk";
import { useSession } from "../session/SessionProvider.tsx";

type DeviceRequest = Awaited<ReturnType<DeviceLinkModule["getRequest"]>>;

export function ConnectApprove(): JSX.Element {
  const { client, signedIn, me, signIn } = useSession();
  const deviceId = window.location.hash.replace(/^#/, "").trim();

  const [request, setRequest] = useState<DeviceRequest>(null);
  const [error, setError] = useState("");
  const [approving, setApproving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!deviceId) {
      setError("This link is missing its device id — ask your agent to run `s3p connect` again.");
      return;
    }
    if (!client) return;
    void (async () => {
      const r = await client.deviceLink.getRequest(deviceId);
      if (r) {
        setRequest(r);
      } else {
        setError(
          "This connect link isn't active (it may have been used already, or timed out). " +
            "Ask your agent to run `s3p connect` again for a fresh one.",
        );
      }
    })();
  }, [deviceId, client]);

  async function approve(): Promise<void> {
    if (!request || !client) return;
    setApproving(true);
    setError("");
    try {
      const session = client.getSession();
      const refreshToken = session?.refresh_token;
      const email = me?.email ?? me?.username ?? "";
      if (!refreshToken || !email) {
        throw new Error("no live session to share — sign out and back in, then retry");
      }
      await client.deviceLink.approve(deviceId, { email, refreshToken });
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setApproving(false);
    }
  }

  return (
    <Stack align="center" pt={80} px="md">
      <Card withBorder radius="md" padding="xl" maw={520} w="100%">
        <Group gap="xs" mb="sm">
          <IconLink size={22} />
          <Title order={2}>Connect a machine</Title>
        </Group>

        {done ? (
          <Alert color="teal" icon={<IconShieldCheck />} title="Connected">
            The machine is signed in as <b>{me?.email}</b>. You can close this tab — its
            terminal will confirm within a few seconds. Revoke any time from{" "}
            <Code>/app → Connect an agent</Code>.
          </Alert>
        ) : error ? (
          <Alert color="red" title="Can't connect">
            {error}
          </Alert>
        ) : !request ? (
          <Group>
            <Loader size="sm" />
            <Text c="dimmed">Looking up the connect request…</Text>
          </Group>
        ) : !signedIn ? (
          <Stack>
            <Text>
              A machine{request.host ? <> (<Code>{request.host}</Code>)</> : null} is asking to
              sign in as you. Sign in first, then approve.
            </Text>
            <Button onClick={() => signIn(`/auth/connect#${deviceId}`)}>Sign in to continue</Button>
          </Stack>
        ) : (
          <Stack>
            <Text>
              A machine{request.host ? <> (<Code>{request.host}</Code>)</> : null} wants to
              deploy and manage projects as <b>{me?.email}</b>. It started asking{" "}
              {new Date(request.createdAt).toLocaleString()}.
            </Text>
            <Text size="sm" c="dimmed">
              Only approve if you just ran <Code>s3p connect</Code> (or asked your agent to).
              The machine gets the same revocable access as an agent code.
            </Text>
            <Group>
              <Button loading={approving} onClick={() => void approve()}>
                Approve — sign that machine in
              </Button>
            </Group>
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
